import { ref, onMounted, onUnmounted } from 'vue';

/**
 * Identify which of two candidate elements actually scrolls (it varies per
 * modal variant's CSS) and track whether its content overflows, so the scroller
 * can become a keyboard-reachable region.
 *
 * @param {import('vue').Ref<HTMLElement|null>} regionRef Candidate scroll element (outer).
 * @param {import('vue').Ref<HTMLElement|null>} areaRef Candidate scroll element (inner).
 * @returns {{
 *   activeScrollerRef: import('vue').Ref<HTMLElement|null>,
 *   isScrollable: import('vue').Ref<boolean>,
 * }}
 */
export function useActiveScroller(regionRef, areaRef) {
  const activeScrollerRef = ref(null);
  const isScrollable = ref(false);
  let observer = null;

  onMounted(() => {
    activeScrollerRef.value = [regionRef.value, areaRef.value].find(
      el => el && ['auto', 'scroll'].includes(getComputedStyle(el).overflowY)
    );

    const el = activeScrollerRef.value;
    if (!el) return;

    const checkOverflow = () => {
      isScrollable.value = el.scrollHeight > el.clientHeight;
    };

    checkOverflow();
    if (isScrollable.value) el.setAttribute('tabindex', '0');

    observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
  });

  onUnmounted(() => {
    observer?.disconnect();
  });

  return { activeScrollerRef, isScrollable };
}
